// axiosConfig.js
import axios from 'axios';
import { API_BASE_URL, buildApiUrl } from './apiConfig';
import { clearLocalSession, redirectToUnifiedAuth } from './utils/unifiedAuth';

const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
    timeout: 15000,
});

const buildLegacyUrl = (path) => {
    return buildApiUrl(path);
};

const clearSessionAndRedirect = () => {
    clearLocalSession();
    redirectToUnifiedAuth('/');
};

let refreshPromise = null;

const parseJwtPayload = (token) => {
    if (!token) {
        return null;
    }

    try {
        const [, payload] = token.split('.');
        if (!payload) {
            return null;
        }
        const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
        const decoded = atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '='));
        return JSON.parse(decoded);
    } catch {
        return null;
    }
};

const isAccessTokenExpiringSoon = (accessToken, thresholdSeconds = 60) => {
    const payload = parseJwtPayload(accessToken);
    const exp = Number(payload?.exp || 0);
    if (!exp) {
        return false;
    }
    const nowSeconds = Math.floor(Date.now() / 1000);
    return exp <= (nowSeconds + thresholdSeconds);
};

const persistAuthTokens = (tokens, previousRefreshToken = '') => {
    if (!tokens?.access) {
        throw new Error('Refresh request did not return access token');
    }

    const nextTokens = { ...tokens };
    if (!nextTokens.refresh && previousRefreshToken) {
        nextTokens.refresh = previousRefreshToken;
    }

    localStorage.setItem('authTokens', JSON.stringify(nextTokens));
    return nextTokens;
};

const refreshAccessToken = async (refreshToken) => {
    if (!refreshToken) {
        throw new Error('Missing refresh token');
    }

    if (!refreshPromise) {
        refreshPromise = authRefreshRequest(refreshToken)
            .then((resp) => {
                if (resp.status !== 200) {
                    throw new Error(`Refresh request failed with status ${resp.status}`);
                }
                return persistAuthTokens(resp.data, refreshToken);
            })
            .finally(() => {
                refreshPromise = null;
            });
    }

    return refreshPromise;
};

export const authLoginRequest = async (username, password) => {
    const payload = { username, password };
    return axios.post(buildLegacyUrl('/api/token/'), payload, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: false,
        timeout: 10000,
    });
};

export const authRefreshRequest = async (refreshToken) => {
    const payload = { refresh: refreshToken };
    return axios.post(buildLegacyUrl('/api/token/refresh/'), payload, {
        withCredentials: false,
        timeout: 10000,
    });
};

// Хелпер: получить path запроса
const getPath = (rawUrl) => {
    try {
        return new URL(rawUrl || '', axiosInstance.defaults.baseURL || window.location.origin).pathname;
    } catch {
        return rawUrl || '';
    }
};

axiosInstance.interceptors.request.use(
    async (config) => {
        try {
            const path = getPath(config.url);

            // Let the browser add the multipart boundary. The instance defaults to
            // JSON, which otherwise turns FormData (including uploaded photos) into
            // a JSON request that Django cannot read through request.FILES.
            if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
                if (typeof config.headers?.delete === 'function') {
                    config.headers.delete('Content-Type');
                } else if (config.headers) {
                    delete config.headers['Content-Type'];
                    delete config.headers['content-type'];
                }
            }

            if (config.method?.toLowerCase() === 'get' && /\/api\/verify-otp\/?$/.test(path)) {
                config.url = buildApiUrl('/api/check-otp-status/');
            }

            const excludedForAuthHeader = [
                /\/api\/token\/?$/,
                /\/api\/token\/refresh\/?$/,
                /\/auth\/login\/?$/,
                /\/auth\/refresh\/?$/,
            ];
            let tokens = JSON.parse(localStorage.getItem('authTokens') || 'null');

            const shouldAttachAuthHeader = !excludedForAuthHeader.some((pattern) => pattern.test(path));

            if (shouldAttachAuthHeader && tokens?.access && tokens?.refresh && isAccessTokenExpiringSoon(tokens.access)) {
                try {
                    tokens = await refreshAccessToken(tokens.refresh);
                } catch (refreshError) {
                    console.error('Failed to refresh token before request:', refreshError);
                    clearSessionAndRedirect();
                    return Promise.reject(refreshError);
                }
            }

            if (tokens?.access && shouldAttachAuthHeader) {
                config.headers['Authorization'] = `Bearer ${tokens.access}`;
            }
        } catch (e) {
            console.error('Error in request interceptor:', e);
            localStorage.removeItem('authTokens');
        }

        return config;
    },
    (error) => Promise.reject(error),
);

const shouldAttemptRefresh = (error) => {
    const status = error?.response?.status;
    if (status === 401) {
        return true;
    }
    if (status !== 403) {
        return false;
    }

    const data = error?.response?.data || {};
    const code = String(data?.code || '').toLowerCase();
    const detail = typeof data?.detail === 'string' ? data.detail.toLowerCase() : '';
    const messages = Array.isArray(data?.messages) ? data.messages : [];
    const hasTokenMessage = messages.some((item) => {
        const message = String(item?.message || '').toLowerCase();
        return message.includes('token');
    });

    return code === 'token_not_valid' || detail.includes('token') || hasTokenMessage;
};

axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config || {};
        const path = getPath(originalRequest.url);

        const isLoginEndpoint =
            /\/api\/token\/?$/.test(path)
            || /\/api\/token\/refresh\/?$/.test(path)
            || /\/api\/auth\/login\/?$/.test(path)
            || /\/api\/auth\/refresh\/?$/.test(path)
            || /\/auth\/login\/?$/.test(path)
            || /\/auth\/refresh\/?$/.test(path);

        if (shouldAttemptRefresh(error) && !originalRequest._retry && !isLoginEndpoint) {
            originalRequest._retry = true;
            try {
                const authTokens = JSON.parse(localStorage.getItem('authTokens') || 'null');
                if (authTokens?.refresh) {
                    const newTokens = await refreshAccessToken(authTokens.refresh);
                    originalRequest.headers = originalRequest.headers || {};
                    originalRequest.headers.Authorization = `Bearer ${newTokens.access}`;
                    return axiosInstance(originalRequest);
                }
            } catch (e) {
                console.error('Failed to refresh token:', e);
                clearSessionAndRedirect();
                return Promise.reject({ message: 'Сессия истекла. Пожалуйста, войдите снова.', status: 401 });
            }

            clearSessionAndRedirect();
            return Promise.reject({ message: 'Сессия истекла. Пожалуйста, войдите снова.', status: 401 });
        }

        return Promise.reject(error);
    },
);
export default axiosInstance;
